import { forwardRef, type MouseEventHandler, type ReactNode } from 'react';
import MuiButton from '@mui/material/Button';
import { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import { Loader } from '../Loader';

export type ButtonShape = 'pill' | 'square';

/** `spaceBetween`: start icon + label at the start, `endIcon` pushed to the end. */
export type ButtonContentAlign = 'center' | 'spaceBetween';

/** `neutral` is a design-system-only color (theme `grey[800]`) — not a MUI palette key. */
export type ButtonColor = MuiButtonProps['color'] | 'neutral';

export type ButtonProps = {
  children?: ReactNode;
  variant?: MuiButtonProps['variant'];
  color?: ButtonColor;
  size?: MuiButtonProps['size'];
  shape?: ButtonShape;
  contentAlign?: ButtonContentAlign;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement>;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    shape = 'pill',
    contentAlign = 'center',
    loading = false,
    startIcon,
    endIcon,
    disabled,
    fullWidth = false,
    type = 'button',
    onClick
  },
  ref
) {
  const resolvedDisabled = disabled || loading;
  const disableRipple = variant === 'text';
  const isNeutral = color === 'neutral';
  const className = [
    shape === 'square' && 'SmButton-shape-square',
    isNeutral && 'SmButton-color-neutral',
    contentAlign === 'spaceBetween' && 'SmButton-align-spaceBetween'
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <MuiButton
      ref={ref}
      type={type}
      variant={variant}
      color={isNeutral ? 'inherit' : color}
      size={size}
      fullWidth={fullWidth}
      disabled={resolvedDisabled}
      disableRipple={disableRipple}
      onClick={onClick}
      {...(className ? { className } : {})}
      startIcon={loading ? <Loader size={16} thickness={5} color="inherit" /> : startIcon}
      endIcon={endIcon}
    >
      {children}
    </MuiButton>
  );
});
